import type { EstimateLineItem } from '@prisma/client';
import { PDFType } from '@prisma/client';
import type { EstimateLineItemFull } from '@services/estimate.service';
import type { IQuoteSpecifications } from '~/server/schema/quote.schema';
import { resolveJobId } from './jobUtils';
import { lineItemTotalCostWithFabrication } from './math';
import { formatInches, titlifyEnum } from './stringHelpers';
import { isNotNullish } from './types';

/**
 * Options for react-pdf's `<Document>` (passed through to pdf.js `getDocument`).
 *
 * Serve pdf.js's bundled standard fonts (LiberationSans, etc.) from our own
 * static assets so it can substitute the PDF standard-14 fonts (Helvetica,
 * Helvetica-Bold, ...) instead of relying on system-installed fonts. iOS
 * (including Chrome on iOS) can't load Helvetica-Bold for pdf.js, which logs
 * "Cannot load system font: Helvetica-Bold" and intermittently crashes/reloads
 * the page.
 *
 * react-pdf reloads the document whenever this object's identity changes, so it
 * is a module-level constant shared by every `<Document>` rather than created
 * inline per render.
 */
export const reactPdfDocumentOptions = {
	standardFontDataUrl: '/standard_fonts/',
};

/**
 * Every pdf.js entry point sets this, and the last one to load wins, so they must all agree.
 * Explicitly https: a protocol-relative URL resolves to http on http://localhost, and jsDelivr's
 * http-to-https redirect carries no CORS header, so the worker fails to load in local dev.
 */
export const getPdfWorkerSrc = (pdfjsVersion: string) => `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjsVersion}/build/pdf.worker.min.mjs`;

function formatEstimateLineItemCount(estimateLineItem: EstimateLineItem) {
	switch (estimateLineItem.priceType) {
		case 'HOURLY':
			return `${estimateLineItem.quantity} hours`;
		default:
			return `${estimateLineItem.quantity} count`;
	}
}

export function stringifyEstimateLineItem(estimateLineItem: EstimateLineItemFull, showItemPrices = false, showExactMeasurements = false) {
	const dimensions =
		+(estimateLineItem.width1 ?? 0) === 0 && +(estimateLineItem.height1 ?? 0) === 0
			? ''
			: showExactMeasurements
				? estimateLineItem.width1Fraction && estimateLineItem.height1Fraction
					? ` (${formatInches(estimateLineItem.width1Fraction)} W x ${formatInches(estimateLineItem.height1Fraction)} H)`
					: ''
				: estimateLineItem.blockHeight && estimateLineItem.blockWidth
					? ` (${estimateLineItem.blockWidth}" W x ${estimateLineItem.blockHeight}" H)`
					: '';
	const spacerThickness =
		estimateLineItem.spacerThickness || estimateLineItem.spacerColor
			? ` [${estimateLineItem.spacerThickness} ${titlifyEnum(estimateLineItem.spacerColor ?? '')} SP] `
			: '';
	const overallThickness = estimateLineItem.overallThickness ? ` [${estimateLineItem.overallThickness} OA] ` : '';

	const techniciansDescription =
		estimateLineItem.numberOfTechnicians && estimateLineItem.name === 'Technicians'
			? `${estimateLineItem.numberOfTechnicians} ${estimateLineItem.name}`
			: estimateLineItem.name;

	let result = `${techniciansDescription} ${
		estimateLineItem.variant ? ' ' + titlifyEnum(estimateLineItem.variant) : ''
	}${dimensions}${spacerThickness}${overallThickness}${estimateLineItem.quantity ? `, ${formatEstimateLineItemCount(estimateLineItem)}` : ''}${
		showItemPrices ? ` ($${lineItemTotalCostWithFabrication(estimateLineItem)})` : ''
	}`;

	if (estimateLineItem.fabrications && estimateLineItem.fabrications.length > 0) {
		estimateLineItem.fabrications.forEach((fabrication) => {
			result += `\n \t \t - ${fabrication.quantity ? fabrication.quantity + ' ' : ''}${fabrication.name}${
				fabrication.variant ? ' ' + titlifyEnum(fabrication.variant) : ''
			} ${dimensions}${estimateLineItem.quantity ? `, ${formatEstimateLineItemCount(estimateLineItem)}` : ''}`;
		});
	}

	return result;
}

export function stringifySpecificationsForQuote(specifications: IQuoteSpecifications, exactMeasurement = false) {
	let dimensions = '';
	if (
		isNotNullish(specifications.height1) &&
		isNotNullish(specifications.width1) &&
		+specifications.height1 === 0 &&
		+specifications.width1 === 0
	) {
		dimensions = '';
	} else if (!exactMeasurement) {
		dimensions =
			specifications.blockHeight && specifications.blockWidth ? ` (${specifications.blockWidth}" W x ${specifications.blockHeight}" H)` : '';
	} else if (specifications.width1Fraction && specifications.height1Fraction) {
		dimensions = ` (${specifications.width1Fraction.replace('"', '')}" W x ${specifications.height1Fraction.replace('"', '')}" H)`;
	}

	const thickness = specifications.thickness ? ` ${specifications.thickness}` : '';
	const type = specifications.type ? ` ${titlifyEnum(specifications.type)}` : '';

	let result = `${dimensions}${thickness}${type}`;
	if (specifications.pane2Thickness || specifications.pane2Type) {
		const pane2Thickness = specifications.pane2Thickness ? ` ${specifications.pane2Thickness}` : '';
		const pane2Type = specifications.pane2Type ? ` ${titlifyEnum(specifications.pane2Type)}` : '';
		const spacer =
			specifications.spacerThickness || specifications.spacerColor
				? `, ${specifications.spacerThickness} ${titlifyEnum(specifications.spacerColor ?? '')} SP`
				: '';
		const overallThickness = specifications.overallThickness ? `, ${specifications.overallThickness} OA` : '';

		result += ` over${pane2Thickness}${pane2Type} IG${spacer}${overallThickness}`;
	}

	const tempering = titlifyEnum(specifications.tempering ?? '');
	const variant = titlifyEnum(specifications.variant ?? '');
	const length = specifications.lengthFraction ? ` (Length: ${specifications.lengthFraction})` : '';
	result = tempering ? `${result} ${tempering}` : result;
	if (variant !== tempering) result = variant ? `${result} ${variant}` : result;
	if (length) result += length;

	return result;
}

export const quoteNumberUtils = () => {
	const prefixOfPdfType = {
		[PDFType.INVOICE]: 'IN-',
		[PDFType.QUOTE]: '',
		[PDFType.RECEIPT]: 'RCT-',
	};

	const removePrefixes = (value: string) => {
		let result = value;
		Object.values(prefixOfPdfType).forEach((prefix) => {
			if (result.startsWith(prefix)) {
				result = result.replace(prefix, '');
			}
		});
		return result;
	};

	const getQuoteNumber = (quoteNumber: string | null | undefined, pdfType: PDFType) => {
		if (!quoteNumber) return '';

		return prefixOfPdfType[pdfType] + removePrefixes(quoteNumber);
	};

	return {
		removePrefixes,
		getQuoteNumber,
	};
};

// The invoice number exactly as the quote/invoice send screen shows it (EstimateQuoteView.getNumberValue):
// the saved Quote.number, or the "{resolved job id}-{estimate version}" default the screen falls back to,
// normalized through getQuoteNumber so the IN- prefix is applied (and any stale prefix stripped). Returns ''
// when there is neither a saved number nor a version to build a default from. Shared by the Finance Center AR
// grids and the customer On-Account tab so the INVOICE # format stays identical everywhere.
export const formatInvoiceNumber = ({
	enableInternalJobId,
	internalJobId,
	jobId,
	number,
	version,
}: {
	enableInternalJobId: boolean;
	internalJobId?: string | null;
	jobId: number | null;
	number?: string | null;
	version: number | null;
}): string => {
	const numberBody =
		number || (version != null && jobId != null ? `${resolveJobId({ enableInternalJobId, internalJobId, jobId })}-${version}` : '');
	return quoteNumberUtils().getQuoteNumber(numberBody, PDFType.INVOICE);
};

export const cleanFileName = (fileName: string) => {
	return fileName.replace(/\s+\.(pdf|png|jpg|jpeg)$/i, '.$1');
};
